import { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box, Button, Container, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import Iconify from '../../../components/iconify';
import { useProject } from '../../../hooks/ProjectContext';
import { exportToCsv, parseCsv } from './RegistryUtils';

const TYPES = ['landowner', 'developer', 'authority', 'community', 'lab', 'vvb', 'extension', 'environmental', 'buyer', 'other'];
const COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'type', label: 'Type' },
  { key: 'organization', label: 'Organization' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'role_desc', label: 'Role' },
];

export default function StakeholdersPage() {
  const { selectedProject } = useProject();
  const [rows, setRows] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState({});
  const fileRef = useRef(null);
  const projectId = selectedProject?.id;

  const load = useCallback(() => {
    if (!projectId) return;
    fetch(`/v1/stakeholders?projectId=${projectId}`)
      .then(r => r.ok ? r.json() : []).then(setRows).catch(() => setRows([]));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    const method = form.id ? 'PUT' : 'POST';
    const url = form.id ? `/v1/stakeholders/${form.id}` : '/v1/stakeholders';
    await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, projectId }) });
    setDlgOpen(false);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    await fetch(`/v1/stakeholders/${id}`, { method: 'DELETE' });
    load();
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const text = await file.text();
    const items = parseCsv(text);
    for (const item of items) {
      if (!item.Name && !item.name) continue;
      await fetch('/v1/stakeholders', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId, name: item.Name || item.name, type: item.Type || item.type || 'other', organization: item.Organization || item.organization || '', email: item.Email || item.email || '', phone: item.Phone || item.phone || '', role_desc: item.Role || item.role_desc || '' }),
      });
    }
    load();
  };

  return (
    <>
      <Helmet><title>Stakeholders | TERRRA</title></Helmet>
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h5">Stakeholders</Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" startIcon={<Iconify icon="eva:download-outline" />}
            onClick={() => exportToCsv('stakeholders.csv', COLUMNS, rows)}>Export</Button>
          <Button size="small" variant="outlined" startIcon={<Iconify icon="eva:upload-outline" />}
            component="label">Import<input hidden type="file" accept=".csv" ref={fileRef} onChange={handleImport} /></Button>
          <Button size="small" variant="contained" startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => { setForm({ type: 'landowner' }); setDlgOpen(true); }}>Add</Button>
        </Stack>

        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map(c => <TableCell key={c.key} sx={{ fontWeight: 700, fontSize: 12 }}>{c.label}</TableCell>)}
                <TableCell sx={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{r.name}</TableCell>
                  <TableCell><Chip label={r.type} size="small" sx={{ fontSize: 10, height: 18 }} /></TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.organization}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.email}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.phone}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.role_desc}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => { setForm(r); setDlgOpen(true); }}><Iconify icon="eva:edit-outline" sx={{ width: 16, height: 16 }} /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(r.id)}><Iconify icon="eva:trash-2-outline" sx={{ width: 16, height: 16, color: 'error.main' }} /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? 'Edit' : 'Add'} Stakeholder</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField size="small" label="Name" fullWidth value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <Select size="small" value={form.type || 'other'} fullWidth onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
              {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
            <TextField size="small" label="Organization" fullWidth value={form.organization || ''} onChange={e => setForm(f => ({ ...f, organization: e.target.value }))} />
            <TextField size="small" label="Email" fullWidth value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            <TextField size="small" label="Phone" fullWidth value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            <TextField size="small" label="Role" multiline rows={2} fullWidth value={form.role_desc || ''} onChange={e => setForm(f => ({ ...f, role_desc: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
